import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hash, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Video-code lookup. Shoppers type the number overlaid on our Instagram/TikTok/
 * Facebook videos and jump straight to that product. `large` = standalone page style.
 */
export const VideoCodeBar = ({ large = false }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const tid = large ? "page-video-code" : "bar-video-code";

  const go = async (e) => {
    e?.preventDefault();
    const n = code.replace(/\D/g, "");
    if (!n || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.get(`/video-code/${n}`);
      navigate(`/product/${r.data.product.slug}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't look up that code — please try again.");
    } finally { setBusy(false); }
  };

  if (large) {
    return (
      <form onSubmit={go} className="w-full max-w-md mx-auto" data-testid={`${tid}-form`}>
        <div className="flex border-2 border-[#1A1A1A] focus-within:border-[#B84A3B] transition-colors bg-white">
          <span className="flex items-center pl-4 text-[#B84A3B]"><Hash size={20} /></span>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoFocus
            placeholder="Enter code, e.g. 108" data-testid={`${tid}-input`}
            className="flex-1 px-3 py-4 text-lg font-body focus:outline-none" />
          <button type="submit" disabled={busy} data-testid={`${tid}-submit`}
            className="bg-[#1A1A1A] text-white px-6 text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-60">
            {busy ? "…" : "Shop It"}
          </button>
        </div>
        {error && <p className="text-sm text-[#B84A3B] font-body mt-3 text-center" data-testid={`${tid}-error`}>{error}</p>}
      </form>
    );
  }

  return (
    <form onSubmit={go} data-testid={`${tid}-form`}
      className="bg-[#F7F7F4] border-b border-[#E5E5DF]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
        <span className="text-[0.68rem] font-mono tracking-[0.14em] uppercase text-[#1A1A1A]">
          Saw a number in our video?
        </span>
        <div className="flex items-center border border-[#1A1A1A] bg-white">
          <span className="pl-2 text-[#B84A3B]"><Hash size={13} /></span>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric"
            placeholder="108" aria-label="Video code" data-testid={`${tid}-input`}
            className="w-16 px-1.5 py-1.5 text-sm font-body focus:outline-none" />
          <button type="submit" disabled={busy} data-testid={`${tid}-submit`}
            className="px-3 py-1.5 bg-[#1A1A1A] text-white text-[0.62rem] font-mono tracking-[0.14em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-60 flex items-center gap-1">
            {busy ? "…" : <>Shop it <ArrowRight size={11} /></>}
          </button>
        </div>
        {error && <span className="text-[0.7rem] text-[#B84A3B] font-body" data-testid={`${tid}-error`}>{error}</span>}
      </div>
    </form>
  );
};
